import { useRosterState } from 'amazon-chime-sdk-component-library-react'
import { DefaultModality } from 'amazon-chime-sdk-js'
import { useMeetingStore } from '@/stores/meeting'

type RosterAttendeeType = {
  chimeAttendeeId: string
  externalUserId?: string
  name?: string
  isDeparted?: boolean
}

type UseRemoteParticipantsReturn = {
  activeRemoteAttendees: RosterAttendeeType[]
  firstRemoteAttendee: RosterAttendeeType | undefined
  hasRemoteParticipants: boolean
}

/**
 * Filtre et retourne les participants distants (excluant l'utilisateur local)
 * @returns Objet contenant la liste des participants distants, le premier participant, et un indicateur de présence
 */
export default function useRemoteParticipants(): UseRemoteParticipantsReturn {
  const { roster } = useRosterState()
  const localAttendeeId = useMeetingStore((state) => state.attendeeId)

  // GUARD: If localAttendeeId isn't set yet, we can't determine remote participants
  // Return empty data to prevent false positives during initialization
  if (!localAttendeeId) {
    return {
      activeRemoteAttendees: [],
      firstRemoteAttendee: undefined,
      hasRemoteParticipants: false
    }
  }

  // Participants actifs (du roster Chime) - FIX: Compare base attendeeIds
  const activeRemoteAttendees = Object.values(roster).filter((attendee) => {
    // Filter out attendees which externalUserId starts with 'aws:' (it's an AWS service like MediaPipeline, ContentShare, etc.)
    if (!attendee || attendee.externalUserId?.startsWith('aws:')) return false

    // Extract base attendeeId without modality suffix (#content, etc.)
    // This ensures we don't count the same user multiple times when they share screen
    const attendeeBaseId = new DefaultModality(attendee.chimeAttendeeId).base()
    const localBaseId = new DefaultModality(localAttendeeId).base()

    return attendeeBaseId !== localBaseId
  })

  return {
    activeRemoteAttendees,
    firstRemoteAttendee: activeRemoteAttendees[0],
    hasRemoteParticipants: activeRemoteAttendees.length > 0 // Compte seulement les actifs
  }
}
